import {
  ContentType,
  type EntityType,
  type UploadMediaCommandInput,
} from '@amzn/gbsa-helios-typescript-client';

/**
 * MIME types the Helios media API accepts. ContentType enum values are
 * the MIME strings themselves, so membership is a direct lookup.
 */
const SUPPORTED_CONTENT_TYPES = new Set<string>(Object.values(ContentType));

/**
 * Extension fallback for browsers that report a missing or nonstandard
 * MIME type (common for csv/doc/mov from some OS/browser pickers).
 * Mirrors the desktop AUDIT_UPLOAD_ACCEPT_TYPES list, minus wmv/flv/mkv
 * which the media API's ContentType enum does not accept.
 */
const CONTENT_TYPE_BY_EXTENSION = new Map<string, ContentType>([
  ['jpg', ContentType.IMAGE_JPEG],
  ['jpeg', ContentType.IMAGE_JPEG],
  ['png', ContentType.IMAGE_PNG],
  ['gif', ContentType.IMAGE_GIF],
  ['webp', ContentType.IMAGE_WEBP],
  ['pdf', ContentType.APPLICATION_PDF],
  ['doc', ContentType.APPLICATION_MSWORD],
  ['docx', ContentType.APPLICATION_DOCX],
  ['xls', ContentType.APPLICATION_EXCEL],
  ['xlsx', ContentType.APPLICATION_XLSX],
  ['txt', ContentType.TEXT_PLAIN],
  ['csv', ContentType.TEXT_CSV],
  ['mp4', ContentType.VIDEO_MP4],
  ['mov', ContentType.VIDEO_QUICKTIME],
  ['avi', ContentType.VIDEO_AVI],
  ['webm', ContentType.VIDEO_WEBM],
]);

/**
 * `accept` attribute value for upload pickers — the uploadable extension
 * list, derived from the same map the content-type fallback uses so the
 * picker and the API can never disagree.
 */
export const MEDIA_UPLOAD_ACCEPT = Array.from(CONTENT_TYPE_BY_EXTENSION.keys())
  .map((extension) => `.${extension}`)
  .join(',');

/** Extensions that map to an image ContentType — the inline-editor path owns
 *  these (embedded as `<img>` in the field HTML), so document-only pickers
 *  exclude them. Derived from the same map so it can't drift. */
const IMAGE_EXTENSIONS = new Set<string>(
  Array.from(CONTENT_TYPE_BY_EXTENSION.entries())
    .filter(([, contentType]) => contentType.startsWith('image/'))
    .map(([extension]) => extension),
);

/**
 * `accept` value for DOCUMENT-only pickers (no images). Attachment pickers that
 * sit beside a rich-text editor use this: images belong inline in the editor
 * (via its image control, which embeds `<img src=mediaId>` the detail view
 * renders), while documents attach as separate files. Mirrors the manual
 * question flow's picker, which is likewise document-only. Derived from
 * {@link MEDIA_UPLOAD_ACCEPT}'s source map minus the image extensions.
 */
export const DOCUMENT_UPLOAD_ACCEPT = Array.from(CONTENT_TYPE_BY_EXTENSION.keys())
  .filter((extension) => !IMAGE_EXTENSIONS.has(extension))
  .map((extension) => `.${extension}`)
  .join(',');

/**
 * Maps a browser-reported MIME type (File.type) to the Helios ContentType
 * enum. Returns undefined for unsupported or missing types so callers can
 * reject the file before uploading.
 */
export function toMediaContentType(mimeType: string): ContentType | undefined {
  return SUPPORTED_CONTENT_TYPES.has(mimeType) ? (mimeType as ContentType) : undefined;
}

/**
 * Resolves the ContentType for a picked file: the browser-reported MIME
 * type when the API supports it, otherwise inferred from the extension.
 */
export function contentTypeForFile(file: {
  type: string;
  name: string;
}): ContentType | undefined {
  const byMimeType = toMediaContentType(file.type);
  if (byMimeType) return byMimeType;
  const extension = file.name.split('.').pop()?.trim().toLowerCase() ?? '';
  return CONTENT_TYPE_BY_EXTENSION.get(extension);
}

export interface UploadMediaContext {
  programId: string;
  businessUnit: string;
  entityType: EntityType;
  createdBy: string;
}

/**
 * Builds an UploadMediaCommand input from a browser File: validates the
 * MIME type, reads the bytes, and strips the extension from the filename
 * (the API stores extension separately, derived from contentType).
 *
 * Throws for unsupported file types — callers should constrain the picker
 * via `accept` so this is a last-resort guard.
 */
export async function fileToUploadMediaInput(
  file: File,
  context: UploadMediaContext,
): Promise<UploadMediaCommandInput> {
  const contentType = contentTypeForFile(file);
  if (!contentType) {
    throw new Error(`Unsupported media type: ${file.type || file.name}`);
  }
  const bytes = new Uint8Array(await file.arrayBuffer());
  const baseName = file.name.replace(/\.[^.]+$/, '').trim();
  return {
    programId: context.programId,
    businessUnit: context.businessUnit,
    entityType: context.entityType,
    createdBy: context.createdBy,
    file: bytes,
    contentType,
    ...(baseName ? { fileName: baseName } : {}),
  };
}
